import { cameraCall, type Recording } from "./client";
import {
  cameraStore,
  clipBlob,
  removeUploaded,
  type Device,
  type LocalClip,
} from "./store";

/** 8 Mbit/s x 150 s = 150 MB, onder de uploadgrens van 175 MB. Hogere bitrate omdat schuim en
 * stromend water bij 4 Mbit/s al zichtbaar blokkerig werden na de dubbele hercompressie
 * (renderer + Instagram). */
const MAX_MS = 150000;
const VIDEO_BITS_PER_SECOND = 8000000;
const MAX_BYTES = 175 * 1024 * 1024;
export class StationRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private clip: LocalClip | null = null;
  private writes: Promise<void> = Promise.resolve();
  private running = false;
  private busy = false;
  private uploadBusy = false;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private watchdog: ReturnType<typeof setInterval> | undefined;
  private lastFrame = Date.now();
  private previousTime = -1;
  private frameCallback: number | undefined;
  private wake: WakeLockSentinel | null = null;
  private bytes = 0;
  private seq = 0;
  private releaseLock: (() => void) | null = null;
  constructor(
    private device: Device,
    private video: HTMLVideoElement,
    private notify: (message: string) => void,
  ) {}

  async enable(audio = false) {
    if (!window.isSecureContext) throw new Error("Camera vereist HTTPS");
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    )
      throw new Error("Browser ondersteunt deze proef niet");
    if (!navigator.locks)
      throw new Error(
        "Browser mist tabbladvergrendeling; gebruik recente Safari",
      );
    // Same stored device token must not operate two recorders in separate tabs.
    await new Promise<void>((resolve, reject) => {
      void navigator.locks
        .request(
          `camera-${this.device.id}`,
          { ifAvailable: true },
          async (lock) => {
            if (!lock) {
              reject(new Error("Camera staat al in een ander tabblad open"));
              return;
            }
            await new Promise<void>((release) => {
              this.releaseLock = release;
              resolve();
            });
          },
        )
        .catch(reject);
    });
    await navigator.storage?.persist?.();
    // Existing pending data must stay intact. Never restart an interrupted recorder after reload.
    const old = await cameraStore.clips
      .where("stationId")
      .equals(this.device.id)
      .toArray();
    for (const c of old.filter((c) => !c.finished)) {
      await cameraStore.clips.update(c.id, {
        finished: true,
        interrupted: true,
        detail: "Pagina herladen; gedeeltelijke opname",
        duration: c.duration || 1,
      });
    }
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 25, max: 30 },
      },
      audio,
    });
    this.video.srcObject = this.stream;
    await this.video.play();
    this.running = true;
    this.lastFrame = Date.now();
    if (this.video.requestVideoFrameCallback) {
      const frame = () => {
        if (!this.running) return;
        this.lastFrame = Date.now();
        this.frameCallback = this.video.requestVideoFrameCallback(frame);
      };
      this.frameCallback = this.video.requestVideoFrameCallback(frame);
    }
    for (const track of this.stream.getTracks()) {
      track.addEventListener("ended", () =>
        this.stop(true, "Cameratrack beëindigd"),
      );
      track.addEventListener("mute", () =>
        this.stop(true, "Camera of microfoon onderbroken"),
      );
    }
    document.addEventListener("visibilitychange", this.visibility);
    window.addEventListener("beforeunload", this.beforeUnload);
    try {
      this.wake = (await navigator.wakeLock?.request("screen")) || null;
    } catch {
      this.notify(
        "Schermvergrendeling voorkomen is niet gelukt. Houd Safari zichtbaar.",
      );
    }
    this.watchdog = setInterval(() => {
      // Watch video presentation rather than container duration (known previous freeze failure).
      if (
        !this.video.requestVideoFrameCallback &&
        this.video.currentTime !== this.previousTime
      ) {
        this.previousTime = this.video.currentTime;
        this.lastFrame = Date.now();
      }
      if (this.clip && Date.now() - this.clip.started >= MAX_MS)
        this.stop(false, "Maximumduur van 2,5 minuut bereikt");
      if (this.clip && Date.now() - this.lastFrame > 8000)
        this.stop(true, "Geen nieuwe videoframes waargenomen");
    }, 1000);
    this.notify(
      "Camera gestart. Houd Safari zichtbaar en het toestel op stroom.",
    );
    void this.loop();
  }
  private visibility = () => {
    if (document.visibilityState !== "visible")
      this.stop(true, "Safari niet meer zichtbaar");
  };
  private beforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = "";
  };
  private settings() {
    const s = this.stream?.getVideoTracks()[0]?.getSettings();
    return {
      width: s?.width,
      height: s?.height,
      frameRate: s?.frameRate,
      audio: !!this.stream?.getAudioTracks().length,
    };
  }
  private snapshot() {
    if (document.visibilityState !== "visible" || !this.video.videoWidth)
      return null;
    const c = document.createElement("canvas");
    c.width = 240;
    c.height = Math.round(
      (240 * this.video.videoHeight) / this.video.videoWidth,
    );
    c.getContext("2d")?.drawImage(this.video, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.45);
  }
  private async loop() {
    if (!this.running) return;
    try {
      const pending = await cameraStore.clips
        .where("stationId")
        .equals(this.device.id)
        .count();
      const ready =
        document.visibilityState === "visible" &&
        !!this.stream
          ?.getVideoTracks()
          .some((t) => t.readyState === "live" && !t.muted) &&
        Date.now() - this.lastFrame < 8000 &&
        !this.busy &&
        !this.uploadBusy &&
        pending === 0;
      const { recording } = await cameraCall(
        "heartbeat",
        { ready, preview: this.snapshot(), settings: this.settings() },
        this.device.token,
      );
      if (this.clip && recording?.id === this.clip.id) {
        if (recording.status === "stop_requested") this.stop();
        else if (recording.status === "queued")
          await cameraCall("ack", { id: recording.id }, this.device.token);
      } else if (this.clip && !recording)
        this.stop(true, "Opdracht niet meer actief");
      else if (recording?.status === "queued" && ready) {
        if (Date.now() - Date.parse(recording.created_at) > 20000) {
          await cameraCall("abandon", { id: recording.id }, this.device.token);
          this.notify(
            "Verouderde startopdracht overgeslagen. Start opnieuw vanaf de tablet.",
          );
        } else await this.start(recording);
      } else if (
        recording?.status === "stop_requested" &&
        pending === 0 &&
        !this.busy
      ) {
        await cameraCall("abandon", { id: recording.id }, this.device.token);
        this.notify("Stop ontvangen vóór opname. Geen beelden gemaakt.");
      } else if (
        recording?.status === "recording" &&
        pending === 0 &&
        !this.busy
      ) {
        await cameraCall("abandon", { id: recording.id }, this.device.token);
        this.notify(
          "Eerdere opname onderbroken zonder bewaarde beelden. Neem opnieuw op.",
        );
      }
      // Upload finished local data even when a previous response was lost.
      await this.uploadPending();
    } catch (e) {
      this.notify(
        `Verbinding/opdracht niet bevestigd: ${e instanceof Error ? e.message : "onbekend"}. Bewaar dit tabblad.`,
      );
    } finally {
      if (this.running) this.timer = setTimeout(() => void this.loop(), 2000);
    }
  }
  private async start(r: Recording) {
    if (!this.stream || this.clip || this.busy) return;
    this.busy = true;
    try {
      const mime = ["video/mp4", "video/webm"].find((m) =>
        MediaRecorder.isTypeSupported(m),
      );
      if (!mime) throw new Error("Geen ondersteund videoformaat");
      const clip: LocalClip = {
        id: r.id,
        stationId: this.device.id,
        mime,
        started: Date.now(),
        duration: 0,
        finished: false,
        interrupted: false,
        detail: "",
        settings: this.settings(),
      };
      await cameraStore.clips.add(clip);
      this.clip = clip;
      this.bytes = 0;
      this.seq = 0;
      this.writes = Promise.resolve();
      const recorder = new MediaRecorder(this.stream, {
        mimeType: mime,
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      });
      this.recorder = recorder;
      recorder.ondataavailable = (event) => {
        if (!event.data.size) return;
        const seq = this.seq++;
        this.bytes += event.data.size;
        this.writes = this.writes
          .then(async () => {
            await cameraStore.chunks.put({
              clipId: clip.id,
              seq,
              bytes: await event.data.arrayBuffer(),
            });
            await cameraStore.clips.update(clip.id, {
              duration: Math.min(195, (Date.now() - clip.started) / 1000),
            });
          })
          .catch(() => {
            this.stop(true, "Lokale opslag faalt; opname mogelijk onvolledig");
            this.notify("Opslagfout: opname mogelijk onvolledig. Wis niets.");
          });
        if (this.bytes > MAX_BYTES)
          this.stop(true, "Maximale bestandsgrootte bereikt");
      };
      recorder.onerror = () => this.stop(true, "Browser meldde opnamefout");
      recorder.onstop = () => {
        void this.finish(clip);
      };
      recorder.onstart = () => {
        this.notify(`Opname loopt voor ${r.wi_name}`);
        void cameraCall("ack", { id: r.id }, this.device.token).catch(() =>
          this.notify(
            "Opname lokaal gestart; serverbevestiging volgt bij herstel verbinding.",
          ),
        );
      };
      recorder.start(2000);
    } catch (e) {
      this.stop(true, "Opname kon niet starten");
      if (this.clip && (!this.recorder || this.recorder.state === "inactive"))
        await this.finish(this.clip);
      throw e;
    } finally {
      this.busy = false;
    }
  }
  stop(interrupted = false, detail = "") {
    if (!this.clip) return;
    this.clip.interrupted ||= interrupted;
    if (detail) this.clip.detail = detail;
    if (this.recorder && this.recorder.state !== "inactive")
      this.recorder.stop();
  }
  private async finish(clip: LocalClip) {
    this.busy = true;
    try {
      await this.writes;
      clip.duration = Math.min(195, (Date.now() - clip.started) / 1000);
      clip.finished = true;
      await cameraStore.clips.put(clip);
      this.clip = null;
      this.recorder = null;
      this.notify(
        "Opname lokaal bewaard. Upload wordt bevestigd zodra de verbinding werkt.",
      );
    } catch {
      this.notify(
        "Lokale afronding mislukt. Houd dit tabblad open; wis niets.",
      );
    } finally {
      this.busy = false;
    }
  }
  async uploadPending() {
    if (this.uploadBusy || this.clip || this.busy) return;
    this.uploadBusy = true;
    try {
      const clips = await cameraStore.clips
        .where("stationId")
        .equals(this.device.id)
        .toArray();
      for (const c of clips.filter((c) => c.finished)) {
        const blob = await clipBlob(c);
        if (!blob.size) {
          await cameraCall("abandon", { id: c.id }, this.device.token);
          await removeUploaded(c.id); // zero bytes only, cancellation preserved on server
          this.notify("Opname bevatte geen beelden. Neem opnieuw op.");
          continue;
        }
        await cameraCall(
          "stopped",
          { id: c.id, interrupted: c.interrupted, detail: c.detail },
          this.device.token,
        );
        const p = await cameraCall(
          "presign",
          {
            id: c.id,
            mimeType: c.mime,
            bytes: blob.size,
            duration: c.duration,
            settings: c.settings,
          },
          this.device.token,
        );
        if (!p.saved) {
          if (!p.uploadUrl) throw new Error("Uploadadres ontbreekt");
          const abort = new AbortController();
          const timer = setTimeout(() => abort.abort(), 180000);
          try {
            const result = await fetch(p.uploadUrl, {
              method: "PUT",
              headers: { "Content-Type": c.mime },
              body: blob,
              signal: abort.signal,
            });
            if (!result.ok) throw new Error("Upload mislukt");
          } finally {
            clearTimeout(timer);
          }
          await cameraCall("complete", { id: c.id }, this.device.token);
        }
        await removeUploaded(c.id);
        this.notify(
          c.interrupted
            ? "Gedeeltelijke opname opgeslagen bij het kleed. Controle nodig."
            : "Opname opgeslagen bij het kleed.",
        );
      }
    } finally {
      this.uploadBusy = false;
    }
  }
  close() {
    this.running = false;
    clearTimeout(this.timer);
    clearInterval(this.watchdog);
    if (this.frameCallback !== undefined)
      this.video.cancelVideoFrameCallback?.(this.frameCallback);
    this.stop(true, "Camerapagina gesloten");
    this.stream?.getTracks().forEach((t) => t.stop());
    document.removeEventListener("visibilitychange", this.visibility);
    window.removeEventListener("beforeunload", this.beforeUnload);
    void this.wake?.release();
    this.releaseLock?.();
    this.releaseLock = null;
  }
}
